import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Logo from "../components/Logo";
import { useMemberAuth } from "../lib/memberAuth";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  return String(detail);
}

export default function AccountLogin() {
  const { login, register, account, loading } = useMemberAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/account";
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && account) navigate(next, { replace: true }); }, [loading, account, navigate, next]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back.");
      } else {
        await register(name, email, password);
        toast.success("Account created. Welcome to The Win Room.");
      }
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Invalid credentials.");
    } finally { setBusy(false); }
  };

  return (
    <section className="min-h-screen flex items-center justify-center px-6 bg-[#0A0A0A]" data-testid="account-login-page">
      <div className="w-full max-w-md py-16">
        <Link to="/" className="inline-block mb-12"><Logo variant="vertical" /></Link>
        <p className="eyebrow">Your Account</p>
        <h1 className="font-serif text-4xl mt-3">{mode === "login" ? "Sign in." : "Create your account."}</h1>
        <p className="text-[var(--twr-warm-white)]/60 text-sm mt-4">
          {mode === "login"
            ? "Members and shop customers sign in here."
            : "Free to create — track orders, verify your age once, and manage your membership."}
        </p>

        <div className="mt-8 flex border border-[var(--twr-line-strong)]">
          <button type="button" data-testid="account-mode-login" onClick={() => setMode("login")}
            className={`flex-1 py-3 text-[11px] tracking-[0.28em] uppercase transition-colors ${mode === "login" ? "bg-[var(--twr-bronze)] text-[#0A0A0A]" : "text-[var(--twr-warm-white)]/65 hover:text-[var(--twr-bronze)]"}`}>
            Sign In
          </button>
          <button type="button" data-testid="account-mode-register" onClick={() => setMode("register")}
            className={`flex-1 py-3 text-[11px] tracking-[0.28em] uppercase transition-colors ${mode === "register" ? "bg-[var(--twr-bronze)] text-[#0A0A0A]" : "text-[var(--twr-warm-white)]/65 hover:text-[var(--twr-bronze)]"}`}>
            Create Account
          </button>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5">
          {mode === "register" && (
            <label className="block">
              <span className="eyebrow">Full Name</span>
              <input data-testid="account-register-name" required value={name} onChange={(e) => setName(e.target.value)}
                className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-sm" />
            </label>
          )}
          <label className="block">
            <span className="eyebrow">Email</span>
            <input data-testid="account-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-sm" />
          </label>
          <label className="block">
            <span className="eyebrow">Password {mode === "register" && "(8+ characters)"}</span>
            <input data-testid="account-login-password" type="password" required minLength={mode === "register" ? 8 : undefined} value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-sm" />
          </label>
          <button disabled={busy} data-testid="account-login-submit" className="btn-bronze w-full">
            {busy ? "One moment…" : mode === "login" ? "Sign In" : "Create Account"}
          </button>
        </form>

        <div className="flex items-center gap-4 my-8">
          <div className="flex-1 h-px bg-[var(--twr-line-strong)]" />
          <span className="text-[var(--twr-warm-white)]/40 text-[11px] tracking-[0.22em] uppercase">or</span>
          <div className="flex-1 h-px bg-[var(--twr-line-strong)]" />
        </div>
        <button
          type="button"
          data-testid="account-google-login"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/account";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="w-full flex items-center justify-center gap-3 border border-[var(--twr-line-strong)] hover:border-[var(--twr-bronze)] hover:text-[var(--twr-bronze)] transition-colors px-4 py-3.5 text-[12px] tracking-[0.22em] uppercase"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.4 1.79 14.94.75 12 .75 7.44.75 3.52 3.36 1.6 7.16l3.66 2.84C6.16 7.16 8.84 5.04 12 5.04z"/><path fill="#4285F4" d="M23.25 12.27c0-.86-.08-1.68-.22-2.47H12v4.68h6.32c-.27 1.46-1.1 2.7-2.34 3.53l3.6 2.79c2.1-1.95 3.67-4.82 3.67-8.53z"/><path fill="#FBBC05" d="M5.26 14.29a7.06 7.06 0 0 1 0-4.58L1.6 6.87a11.26 11.26 0 0 0 0 10.26l3.66-2.84z"/><path fill="#34A853" d="M12 23.25c3.04 0 5.6-1 7.46-2.72l-3.6-2.79c-1 .68-2.3 1.08-3.86 1.08-3.16 0-5.84-2.12-6.74-4.97l-3.66 2.84c1.92 3.8 5.84 6.56 10.4 6.56z"/></svg>
          Continue with Google
        </button>
        <p className="text-[var(--twr-warm-white)]/40 text-[11px] mt-8 leading-relaxed">
          Membership is by invitation. If you'd like to apply, <Link to="/membership" className="text-[var(--twr-bronze)]">visit the membership page</Link>.
        </p>
      </div>
    </section>
  );
}
